<script lang="ts" setup>
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import { inputAutoWidth as vInputAutoWidth } from '@/common/directives/inputAutoWidth';
import { TOKEN_STATUS } from '@/common/components/molecules/filterSearch/filterSearch.define';
import {
  Props,
  Emit,
  setup,
} from '@/common/components/molecules/filterSearch/filterSearchToken.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  token,
  isEditingTokenParts,
  isInvalidToken,
  isWritingToken,
  onInput,
  onKeyEnter,
  onKeyUpDown,
  onKeyEsc,
  onKeyBackspace,
  onClickTokenParts,
  onKeyCtrlEnter,
  onClickAddMultiValue,
  onClickRemoveToken,
  onClickToken,
} = setup(props, emit);
</script>

<template>
  <div
    :class="[
      'filter-search-token',
      {
        'filter-search-token--invalid': isInvalidToken(),
        'filter-search-token--editing': isWritingToken(),
      },
    ]"
    @click.stop="onClickToken"
  >
    <template v-if="token.key?.id">
      <span class="token-parts token-parts--key">
        {{ token.key.name }}
      </span>
    </template>
    <input
      v-else-if="isEditingTokenParts('key')"
      v-input-auto-width
      @input="onInput"
      @keydown.enter.exact="onKeyEnter"
      @keydown.up.exact="(event) => onKeyUpDown(event, 'up')"
      @keydown.down.exact="(event) => onKeyUpDown(event, 'down')"
      @keydown.esc.exact="onKeyEsc"
      @keydown.delete.exact="onKeyBackspace"
    />

    <input
      v-if="isEditingTokenParts('operator')"
      v-input-auto-width
      :placeholder="token.operator?.name"
      @input="onInput"
      @keydown.enter.exact="onKeyEnter"
      @keydown.up.exact="(event) => onKeyUpDown(event, 'up')"
      @keydown.down.exact="(event) => onKeyUpDown(event, 'down')"
      @keydown.esc.exact="onKeyEsc"
      @keydown.delete.exact="onKeyBackspace"
    />
    <span
      v-else-if="token.operator"
      class="token-parts token-parts--operator"
      @click.stop="() => onClickTokenParts('operator')"
    >
      {{ token.operator?.name }}
    </span>

    <template v-if="token.values?.length || isEditingTokenParts('value')">
      <input
        v-if="isEditingTokenParts('value')"
        v-input-auto-width
        :placeholder="token.values?.map(({ name }) => name).join(',')"
        @input="onInput"
        @keydown.enter.exact="onKeyEnter"
        @keydown.up.exact="onKeyUpDown($event, 'up')"
        @keydown.down.exact="onKeyUpDown($event, 'down')"
        @keydown.esc.exact="onKeyEsc"
        @keydown.ctrl.enter.exact="onKeyCtrlEnter"
        @keydown.delete.exact="onKeyBackspace"
      />
      <span
        v-else
        class="token-parts token-parts--value"
        @click.stop="() => onClickTokenParts('value')"
      >
        {{ token.values?.map(({ name }) => name).join(',') }}
      </span>
    </template>

    <base-button
      v-if="props.isActiveAddMultiValueBtn && tokenData.status !== TOKEN_STATUS.KEY_DONE"
      type="icon"
      size="xsmall"
      class="filter-search-token__button"
      @click.stop="onClickAddMultiValue"
    >
      <ev-icon class="icon-plus-square" />
    </base-button>

    <base-button
      type="icon"
      size="xsmall"
      class="filter-search-token__button"
      @click.stop="onClickRemoveToken"
    >
      <ev-icon class="icon-close" />
    </base-button>
  </div>
</template>
<style lang="scss">
.filter-search-token {
  display: flex;
  position: relative;
  gap: 1px;
  align-items: center;
  padding: 1px 4px;
  border: 1px solid var(--color-blue-05-05);
  border-radius: 2px;
  background-color: var(--color-blue-10-00);
  font-size: 11px;
  line-height: 16px;
  color: var(--color-w-gray-11);
  white-space: nowrap;

  &--invalid {
    border-color: var(--color-red-05-05);
    background-color: var(--color-red-10-00);
  }

  &--editing {
    border-color: var(--color-blue-05-05);
    background-color: var(--color-blue-10-00);
  }

  .token-parts {
    display: inline-block;
    height: 15px;
    padding: 0 2px;
    overflow: hidden;
    border-radius: 2px;
    color: var(--color-gray-01-11);
    text-overflow: ellipsis;

    &--value {
      flex: auto;
    }

    &--key,
    &--operator {
      flex-grow: 0;
      flex-shrink: 0;
    }

    &--operator,
    &--value {
      cursor: pointer;
      color: var(--color-blue-04-05);
      white-space: nowrap;

      &:hover {
        background-color: var(--color-blue-08-02);
      }

      &:active {
        background-color: var(--filter-search-token-editable-parts-active-bg-color);
      }
    }
  }

  &__button.ev-button.type-icon.size-xsmall {
    flex-grow: 0;
    flex-shrink: 0;

    .icon-plus-square {
      background-color: var(--color-gray-02-09) !important;
    }

    .icon-close {
      background-color: var(--color-gray-02-09) !important;
    }

    &.type-icon:hover:not(.selected, :active, :disabled, .disabled) {
      background-color: var(--color-blue-08-02) !important;
    }
  }

  input {
    min-width: 0;
    height: 100%;
    padding: 0 5px;
    outline: none;
    background-color: transparent !important;
    color: var(--main-font-color) !important;
    caret-color: var(--color-blue-05-05);

    &::placeholder {
      color: var(--color-gray-03-09);
    }
  }
}
</style>
